import { Container, Graphics } from 'pixi.js';
import { Boat, type BoatShape } from './boat.js';
import { angleToDir, type Vec2 } from './physics.js';
import { WORLD_W, WORLD_H } from './world.js';
import { drawPerson } from './person.js';
import type { RemotePlayer } from './net.js';
import { drawAnimalOnBoat, type AnimalType, type Animal } from './animals.js';
import { drawPet, type PetType } from './pets.js';

const DEFAULT_REMOTE_COLOR = 0xc9a3a3;
const ARROW_SIZE = 12;
const ARROW_MARGIN = 30;

function wrapDelta(d: number, size: number): number {
  if (d > size / 2) return d - size;
  if (d < -size / 2) return d + size;
  return d;
}

interface BoatEntry {
  container: Container;
  boat: Boat;
  color: number;
  shirtColor: number;
  hairStyle: number;
  hairColor: number;
  boatShape: string;
  name: string;
  personGfx: Graphics;
  boatAnimalGfx: Graphics;
  petGfx: Graphics;
  petOff: { x: number; y: number };
  petAngle: number;
  petWalkTimer: number;
  pet2Gfx: Graphics;
  pet2Off: { x: number; y: number };
  pet2Angle: number;
  pet2WalkTimer: number;
  // Snapshot interpolation: lerp from prev → curr over the interval
  prev: { x: number; y: number; angle: number; sailAngle: number; speed: number };
  curr: { x: number; y: number; angle: number; sailAngle: number; speed: number };
  snapshotTime: number; // timestamp when curr was received
}

function lerpAngle(a: number, b: number, t: number): number {
  let delta = b - a;
  delta -= Math.round(delta / (2 * Math.PI)) * 2 * Math.PI;
  return a + delta * t;
}

export class RemoteBoats {
  private worldLayer: Container;
  private arrowGfx: Graphics;
  private boats = new Map<string, BoatEntry>();
  animalsRef: Animal[] | null = null;
  /** Set from main.ts so remote pets can respect land/bridges when their owner is ashore. */
  isWalkableFn: ((p: { x: number; y: number }) => boolean) | null = null;

  constructor(worldContainer: Container, stage: Container) {
    this.worldLayer = new Container();
    worldContainer.addChild(this.worldLayer);

    this.arrowGfx = new Graphics();
    stage.addChild(this.arrowGfx);
  }

  update(
    players: Map<string, RemotePlayer>,
    boatPos: Vec2,
    screenW: number,
    screenH: number,
    dt: number = 1,
  ): void {
    // Remove boats for players that left
    for (const [id, entry] of this.boats) {
      if (!players.has(id)) {
        this.worldLayer.removeChild(entry.container);
        this.worldLayer.removeChild(entry.personGfx);
        this.worldLayer.removeChild(entry.boatAnimalGfx);
        this.worldLayer.removeChild(entry.petGfx);
        this.worldLayer.removeChild(entry.pet2Gfx);
        entry.container.destroy({ children: true });
        entry.personGfx.destroy();
        entry.boatAnimalGfx.destroy();
        entry.petGfx.destroy();
        entry.pet2Gfx.destroy();
        this.boats.delete(id);
      }
    }

    this.arrowGfx.clear();
    const cx = screenW / 2;
    const cy = screenH / 2;

    const now = performance.now();
    const SNAPSHOT_INTERVAL = 50; // server broadcasts at 20Hz

    for (const [id, p] of players) {
      // Ensure boat exists
      let entry = this.boats.get(id);
      const pColor = p.color || DEFAULT_REMOTE_COLOR;
      const pShirt = p.shirtColor || 0x2255aa;
      const pHair = p.hairStyle ?? 1;
      const pHairColor = p.hairColor ?? 0x4a3520;
      const pBoatShape = p.boatShape || 'classic';
      const pName = p.name || '';
      if (!entry) {
        const snap = { x: p.x, y: p.y, angle: p.angle, sailAngle: p.sailAngle, speed: p.speed };
        const boat = new Boat(pColor, pShirt, pHair, pName, pBoatShape as BoatShape, pHairColor);
        const container = new Container();
        container.addChild(boat.container);
        const personGfx = new Graphics();
        const boatAnimalGfx = new Graphics();
        const petGfx = new Graphics();
        const pet2Gfx = new Graphics();
        this.worldLayer.addChild(container);
        this.worldLayer.addChild(personGfx);
        this.worldLayer.addChild(boatAnimalGfx);
        this.worldLayer.addChild(petGfx);
        this.worldLayer.addChild(pet2Gfx);
        entry = {
          container, boat, color: pColor, shirtColor: pShirt, hairStyle: pHair, hairColor: pHairColor, boatShape: pBoatShape, name: pName,
          personGfx, boatAnimalGfx, petGfx, pet2Gfx,
          prev: { ...snap }, curr: { ...snap }, snapshotTime: now,
          petOff: { x: 10, y: 8 },  petAngle: Math.random() * Math.PI * 2,  petWalkTimer: 0,
          pet2Off: { x: -10, y: 8 }, pet2Angle: Math.random() * Math.PI * 2, pet2WalkTimer: 0,
        };
        this.boats.set(id, entry);
      } else if (entry.color !== pColor || entry.shirtColor !== pShirt || entry.hairStyle !== pHair || entry.hairColor !== pHairColor || entry.boatShape !== pBoatShape || entry.name !== pName) {
        // Player changed appearance — recreate boat
        entry.container.removeChild(entry.boat.container);
        entry.boat.container.destroy({ children: true });
        entry.boat = new Boat(pColor, pShirt, pHair, pName, pBoatShape as BoatShape, pHairColor);
        entry.container.addChild(entry.boat.container);
        entry.color = pColor;
        entry.shirtColor = pShirt;
        entry.hairStyle = pHair;
        entry.hairColor = pHairColor;
        entry.boatShape = pBoatShape;
        entry.name = pName;
      }

      // Detect new snapshot from server (position changed)
      if (p.x !== entry.curr.x || p.y !== entry.curr.y ||
          p.angle !== entry.curr.angle || p.sailAngle !== entry.curr.sailAngle) {
        entry.prev = { ...entry.curr };
        entry.curr = { x: p.x, y: p.y, angle: p.angle, sailAngle: p.sailAngle, speed: p.speed };
        entry.snapshotTime = now;
      }

      // Interpolate between prev and curr, then extrapolate beyond
      const elapsed = now - entry.snapshotTime;
      const t = elapsed / SNAPSHOT_INTERVAL;
      const dxSnap = wrapDelta(entry.curr.x - entry.prev.x, WORLD_W);
      const dySnap = wrapDelta(entry.curr.y - entry.prev.y, WORLD_H);

      let ix: number, iy: number, iAngle: number, iSailAngle: number, iSpeed: number;
      if (t <= 1) {
        // Interpolate
        ix = entry.prev.x + dxSnap * t;
        iy = entry.prev.y + dySnap * t;
        iAngle = lerpAngle(entry.prev.angle, entry.curr.angle, t);
        iSailAngle = lerpAngle(entry.prev.sailAngle, entry.curr.sailAngle, t);
        iSpeed = entry.prev.speed + (entry.curr.speed - entry.prev.speed) * t;
      } else {
        // Extrapolate using prev→curr velocity (cap at 2x interval to avoid runaway)
        const et = Math.min(t, 2) - 1;
        ix = entry.curr.x + dxSnap * et;
        iy = entry.curr.y + dySnap * et;
        iAngle = entry.curr.angle;
        iSailAngle = entry.curr.sailAngle;
        iSpeed = entry.curr.speed;
      }
      ix = ((ix % WORLD_W) + WORLD_W) % WORLD_W;
      iy = ((iy % WORLD_H) + WORLD_H) % WORLD_H;

      // If this player is inside a building, hide them entirely (boat, person,
      // pets). They'll reappear when they step back out.
      if (p.inside) {
        entry.container.visible = false;
        entry.personGfx.clear();
        entry.boatAnimalGfx.clear();
        entry.petGfx.clear();
        entry.pet2Gfx.clear();
        continue;
      }
      entry.container.visible = true;

      // Position in world coords
      entry.container.x = ix;
      entry.container.y = iy;

      // Update visuals
      const boatDir = angleToDir(iAngle);
      const sailDir = angleToDir(iSailAngle);
      entry.boat.update(boatDir, sailDir, iSpeed);

      // Draw remote person when ashore
      entry.personGfx.clear();
      if (p.ashore) {
        entry.boat.setPersonVisible(false);
        drawPerson(entry.personGfx, p.personX, p.personY, pShirt, pHair, 0, 1, { hairColor: pHairColor });
      } else {
        entry.boat.setPersonVisible(true);
      }

      // Shared helper: step a pet forward like a chicken, respecting leash
      // and (when owner is ashore) land/bridge walkability.
      const stepPet = (
        off: { x: number; y: number },
        state: { angle: number; timer: number },
        anchorX: number,
        anchorY: number,
      ): void => {
        state.timer -= dt;
        if (state.timer <= 0) {
          state.angle = Math.random() * Math.PI * 2;
          state.timer = 80 + Math.random() * 200;
        }
        const SPEED = 0.5;
        const nOffX = off.x + Math.cos(state.angle) * SPEED * dt;
        const nOffY = off.y + Math.sin(state.angle) * SPEED * dt;
        const absX = anchorX + nOffX;
        const absY = anchorY + nOffY;
        const leash2 = nOffX * nOffX + nOffY * nOffY;
        let blocked = leash2 > 50 * 50;
        if (!blocked && p.ashore && this.isWalkableFn && !this.isWalkableFn({ x: absX, y: absY })) {
          blocked = true;
        }
        if (blocked) {
          state.angle += Math.PI + (Math.random() - 0.5) * 1.2;
          state.timer = 40 + Math.random() * 80;
        } else {
          off.x = nOffX;
          off.y = nOffY;
        }
      };

      const petAx = p.ashore ? p.personX : ix;
      const petAy = p.ashore ? p.personY : iy;

      // If server sent synced pet offsets, use them (so everyone sees pets in
      // the same place). Otherwise fall back to local chicken-walk sim.
      entry.petGfx.clear();
      if (p.petType) {
        let offX: number, offY: number;
        if (typeof p.petOffX === 'number' && typeof p.petOffY === 'number') {
          offX = p.petOffX; offY = p.petOffY;
          entry.petOff.x = offX; entry.petOff.y = offY;
        } else {
          const s = { angle: entry.petAngle, timer: entry.petWalkTimer };
          stepPet(entry.petOff, s, petAx, petAy);
          entry.petAngle = s.angle; entry.petWalkTimer = s.timer;
          offX = entry.petOff.x; offY = entry.petOff.y;
        }
        drawPet(entry.petGfx, p.petType as PetType, petAx + offX, petAy + offY, p.catColor ?? undefined);
      }

      entry.pet2Gfx.clear();
      if (p.petType2) {
        let offX: number, offY: number;
        if (typeof p.pet2OffX === 'number' && typeof p.pet2OffY === 'number') {
          offX = p.pet2OffX; offY = p.pet2OffY;
          entry.pet2Off.x = offX; entry.pet2Off.y = offY;
        } else {
          const s = { angle: entry.pet2Angle, timer: entry.pet2WalkTimer };
          stepPet(entry.pet2Off, s, petAx, petAy);
          entry.pet2Angle = s.angle; entry.pet2WalkTimer = s.timer;
          offX = entry.pet2Off.x; offY = entry.pet2Off.y;
        }
        drawPet(entry.pet2Gfx, p.petType2 as PetType, petAx + offX, petAy + offY, p.catColor ?? undefined);
      }

      // Draw animal on remote boat
      entry.boatAnimalGfx.clear();
      if (!p.ashore && p.boatAnimalIdx != null) {
        // We need the animal type — passed as index, use a lookup array stored externally.
        // We use a global animals array injected via update().
        if (this.animalsRef) {
          const a = this.animalsRef[p.boatAnimalIdx];
          if (a) drawAnimalOnBoat(entry.boatAnimalGfx, a.type as AnimalType, ix + 8, iy + 4);
        }
      }

      // Edge arrow if off-screen (use shortest wrapped distance)
      const wdx = wrapDelta(ix - boatPos.x, WORLD_W);
      const wdy = wrapDelta(iy - boatPos.y, WORLD_H);
      const screenX = wdx + cx;
      const screenY = wdy + cy;

      const margin = 40;
      if (screenX >= margin && screenX <= screenW - margin &&
          screenY >= margin && screenY <= screenH - margin) {
        continue; // on screen, no arrow needed
      }

      const dx = wdx;
      const dy = wdy;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;

      const hx = cx - ARROW_MARGIN;
      const hy = cy - ARROW_MARGIN;

      let ray = Infinity;
      if (dx > 0) ray = Math.min(ray, hx / dx);
      if (dx < 0) ray = Math.min(ray, -hx / dx);
      if (dy > 0) ray = Math.min(ray, hy / dy);
      if (dy < 0) ray = Math.min(ray, -hy / dy);

      const ax = cx + dx * ray;
      const ay = cy + dy * ray;
      const angle = Math.atan2(dy, dx);

      // Draw arrow triangle pointing toward player (pre-rotated vertices)
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const tip = { x: ARROW_SIZE, y: 0 };
      const bl = { x: -ARROW_SIZE * 0.5, y: -ARROW_SIZE * 0.6 };
      const br = { x: -ARROW_SIZE * 0.5, y: ARROW_SIZE * 0.6 };
      const rot = (p: { x: number; y: number }) => ({
        x: ax + p.x * cos - p.y * sin,
        y: ay + p.x * sin + p.y * cos,
      });
      const t0 = rot(tip), t1 = rot(bl), t2 = rot(br);
      this.arrowGfx.moveTo(t0.x, t0.y);
      this.arrowGfx.lineTo(t1.x, t1.y);
      this.arrowGfx.lineTo(t2.x, t2.y);
      this.arrowGfx.closePath();
      this.arrowGfx.fill({ color: 0xe85050, alpha: 0.85 });
      this.arrowGfx.stroke({ color: 0xffffff, width: 1.5, alpha: 0.6 });
    }
  }
}
